// SQLite identifier helpers: double-quote / bracket / backtick round-trip.
//
// SQLite identifier rules (per the sqlite.org docs "Keywords And Identifiers"):
//   * Bare identifiers match `^[A-Za-z_][A-Za-z0-9_$]*$`.
//   * `"name"` is the standard SQL form. Doubled-quote escape (`""`) for a
//     literal `"` inside the body. SQLite ALSO accepts `"name"` as a string
//     literal when the identifier is unknown — but that's a parser fallback
//     for compatibility with broken legacy SQL. The resolver emits double
//     quotes for identifier output because (a) it's the ANSI / always-safe
//     form and (b) any name that needs quoting is by definition not a
//     "common keyword that might collide" — so the fallback path doesn't
//     apply.
//   * `[name]` (MSSQL-style brackets) — accepted by SQLite for compatibility
//     with SQL Server. `]` inside is doubled. Asymmetric brackets so the
//     escape only applies to the closing form.
//   * `` `name` `` (MySQL-style backticks) — accepted by SQLite for
//     compatibility with MySQL. Backslash escape is NOT supported; doubled-
//     backtick escape IS.
//   * Identifier comparison is case-insensitive for ASCII letters by
//     default. Unicode case folding is not applied (parity with SQLite's
//     `nocase` collation behaviour).
//
// Pure module — no Monaco runtime, no I/O.

const BARE_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_$]*$/;

export function needsDoubleQuote(identifier: string): boolean {
  if (identifier === '') return true;
  return !BARE_IDENT_RE.test(identifier);
}

export function doubleQuote(identifier: string): string {
  return `"${identifier.replace(/"/g, '""')}"`;
}

/** Round-trip-safe SQLite identifier — double-quoted only when required. */
export function formatIdentifier(identifier: string): string {
  return needsDoubleQuote(identifier) ? doubleQuote(identifier) : identifier;
}

/** Compose `database.name` with per-part quote-on-demand (e.g. `main.users`). */
export function formatQualifiedTwoPart(database: string, name: string): string {
  return `${formatIdentifier(database)}.${formatIdentifier(name)}`;
}

/**
 * Strip the surrounding quotes from a single identifier token, applying the
 * doubled-escape rule for whichever quote form was used. SQLite accepts:
 *   • `"name"` — doubled `""` escape.
 *   • `[name]` — doubled `]]` escape (asymmetric).
 *   • `` `name` `` — doubled `` `` `` escape.
 *
 * Returns the input verbatim when not quoted — caller is responsible for
 * case-folding bare identifiers if a case-insensitive comparison is required
 * (use `eqIdentifier` / `foldIdentifier`).
 */
export function unquoteIdentifier(token: string): string {
  if (token.length < 2) return token;
  const first = token[0]!;
  const last = token[token.length - 1]!;
  if (first === '"' && last === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  if (first === '[' && last === ']') {
    return token.slice(1, -1).replace(/]]/g, ']');
  }
  if (first === '`' && last === '`') {
    return token.slice(1, -1).replace(/``/g, '`');
  }
  return token;
}

/**
 * SQLite identifier folding for case-comparison purposes.
 *
 * Quoted forms (`"…"`, `[…]`, `` `…` ``) preserve case verbatim after
 * unquoting. Unquoted identifiers fold to lower-case.
 *
 * SQLite is case-insensitive for ASCII identifiers regardless of quoting in
 * practice (the engine's identifier-equality predicate is `nocase`), but
 * preserving case for explicitly-quoted forms matches the user's typed
 * intent — so a user writing `"Album"` sees only that literal column case
 * round-tripped.
 */
export function foldIdentifier(token: string): string {
  if (token.length >= 2) {
    const first = token[0]!;
    const last = token[token.length - 1]!;
    if (first === '"' && last === '"') {
      return token.slice(1, -1).replace(/""/g, '"').toLowerCase();
    }
    if (first === '[' && last === ']') {
      return token.slice(1, -1).replace(/]]/g, ']').toLowerCase();
    }
    if (first === '`' && last === '`') {
      return token.slice(1, -1).replace(/``/g, '`').toLowerCase();
    }
  }
  return token.toLowerCase();
}

/**
 * SQLite identifier equality. Per the engine, ASCII identifier comparison
 * is case-insensitive regardless of quoting form — so `"Album"` ≡ `album` ≡
 * `[ALBUM]` ≡ `` `album` ``. This matches user expectation for SQLite (the
 * `nocase` collation is the default for identifier names).
 */
export function eqIdentifier(a: string, b: string): boolean {
  return foldIdentifier(a) === foldIdentifier(b);
}
